import { apiGet, apiPost } from "./client";

export type TagDocType =
  | "sales_order"
  | "sales_invoice"
  | "purchase_invoice"
  | "purchase_order"
  | "goods_receipt"
  | "sales_delivery"
  | "sales_shipment"
  | "purchase_shipment"
  | "sales_return"
  | "supplier_payment"
  | "customer_payment"
  | "stock_conversion"
  | "journal_entry"
  | "pos_ticket"
  | "customer"
  | "supplier"
  | "product"
  | "override_request"
  | "change_request";

export type TagRow = {
  id: string;
  name: string;
  color: string | null;
};

export type AssigneeRow = {
  userId: string;
  label: string;
};

export function listTags(companyRef: string) {
  return apiGet<{ tags: TagRow[] }>(`/c/${companyRef}/tags`);
}

export function createTag(
  companyRef: string,
  body: { name: string; color?: string },
) {
  return apiPost<{ tag: TagRow; reused: boolean }>(
    `/c/${companyRef}/tags`,
    body,
  );
}

export function getDocMeta(
  companyRef: string,
  docType: TagDocType,
  docId: string,
) {
  const q = new URLSearchParams({ docType, docId });
  return apiGet<{ tags: TagRow[]; assignees: AssigneeRow[] }>(
    `/c/${companyRef}/doc-meta?${q.toString()}`,
  );
}

export function addDocTag(
  companyRef: string,
  body: {
    docType: TagDocType;
    docId: string;
    tagId?: string;
    tagName?: string;
  },
) {
  return apiPost<{ tag: TagRow }>(`/c/${companyRef}/doc-tags`, body);
}

export function removeDocTag(
  companyRef: string,
  body: { docType: TagDocType; docId: string; tagId: string },
) {
  return apiPost<{ ok: true }>(`/c/${companyRef}/doc-tags/remove`, body);
}

export function addDocAssignee(
  companyRef: string,
  body: {
    docType: TagDocType;
    docId: string;
    userId: string;
    linkPath?: string;
    docLabel?: string;
  },
) {
  return apiPost<{ assignee: AssigneeRow }>(
    `/c/${companyRef}/doc-assignees`,
    body,
  );
}

export function removeDocAssignee(
  companyRef: string,
  body: { docType: TagDocType; docId: string; userId: string },
) {
  return apiPost<{ ok: true }>(`/c/${companyRef}/doc-assignees/remove`, body);
}

export function filterDocIds(
  companyRef: string,
  opts: {
    docType: TagDocType;
    tagId?: string;
    assignedToMe?: boolean;
    unassigned?: boolean;
  },
) {
  const q = new URLSearchParams({ docType: opts.docType });
  if (opts.tagId) q.set("tagId", opts.tagId);
  if (opts.assignedToMe) q.set("assignedToMe", "1");
  if (opts.unassigned) q.set("unassigned", "1");
  return apiGet<{
    mode: "include" | "exclude";
    docIds: string[];
    unfiltered?: boolean;
  }>(`/c/${companyRef}/doc-ids?${q.toString()}`);
}

export function batchDocTags(
  companyRef: string,
  docType: TagDocType,
  docIds: string[],
) {
  if (docIds.length === 0) {
    return Promise.resolve({ byDoc: {} as Record<string, TagRow[]> });
  }
  const q = new URLSearchParams({
    docType,
    docIds: docIds.join(","),
  });
  return apiGet<{ byDoc: Record<string, TagRow[]> }>(
    `/c/${companyRef}/doc-tags/batch?${q.toString()}`,
  );
}

export function batchDocAssignees(
  companyRef: string,
  docType: TagDocType,
  docIds: string[],
) {
  if (docIds.length === 0) {
    return Promise.resolve({ byDoc: {} as Record<string, AssigneeRow[]> });
  }
  const q = new URLSearchParams({
    docType,
    docIds: docIds.join(","),
  });
  return apiGet<{ byDoc: Record<string, AssigneeRow[]> }>(
    `/c/${companyRef}/doc-assignees/batch?${q.toString()}`,
  );
}
